import { useState, useMemo } from "react";
import type { QuoteScenario, ScenarioMode } from "@/data/quoteTypes";
import type { ProjectConfiguration } from "@/data/types";
import { calculatePrice, type ComplexityFactors, type PricingOverrides } from "@/data/pricingEngine";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { usePricing } from "@/data/PricingContext";
import { ProjectConfigurator } from "@/components/ProjectConfigurator";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Copy, Layers, CheckCircle, Trash2, Edit, ArrowLeft, Star, Archive,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CONFIG: ProjectConfiguration = {
  layoutType: "", wallCount: 3, hasWindow: false, obstructions: [],
  wallMeasurements: { wallA: { width: "", height: "" }, wallB: { width: "", height: "" }, wallC: { width: "", height: "" }, additional: [] },
  structuralChanges: { removingWalls: false, addingWalls: false, movingDrain: false, movingPlumbing: false, subfloorMod: false, windowMod: "N/A" },
  autoProjectType: "", enclosureType: "",
  selectedVerticals: [], bathroomScopes: [], bathroomPhase2Addons: [], bathroomMaterials: [],
  bathroomSystemType: "", bathroomBaseSize: "", bathroomCustomSize: "",
  baseWidth: "30", baseLength: "60", baseType: "", baseStyle: "",
  laborComplexity: "standard", plumbingRelocation: false, customModifications: false,
  manualAdjustment: "", manualAdjustmentNotes: "",
  selectedWallSystem: "", selectedBase: "", selectedGrabBars: [],
  selectedGlass: "", selectedDoor: "", selectedFixture: "",
  selectedAddOns: [], customQuoteItems: [],
  projectCategory: "", workDescription: "", areas: [],
  totalSqFt: "", roomCount: "", measurementNotes: "",
  primaryMaterial: "", materialGrade: "", colorSelection: "", wallColor: "", baseColor: "", drainLocation: "", additionalOptions: [],
  basePrice: "", discount: "", discountType: "percent",
  hardwareFinish: "", hardwareOverrides: {}, discounts: { military: false, senior: false, frontliner: false },
  customAddOns: [], noAddOnsSelected: false, consultationCategory: "",
  fullBathroomSelections: { flooring: "", toilet: "", vanity: "" },
  fullBathroomAddOns: [], fullBathroomCustomAddOns: [],
  windowCount: 0, windowTypes: [], doorEntryType: "", doorSize: "",
  additionalCharges: "", additionalChargesNote: "",
  specialConditions: "", customerRequests: "", internalNotes: "",
  status: "draft", lastUpdated: new Date().toISOString(),
};

interface QuoteComparisonProps {
  scenarios: QuoteScenario[];
  baseConfig?: ProjectConfiguration;
  quoteId: string;
  isLocked: boolean;
  onScenariosChange: (scenarios: QuoteScenario[]) => void;
  onSelectScenario: (scenario: QuoteScenario) => void;
  onBack: () => void;
}

function ScenarioPrice({ config, allOptions, overrides }: {
  config: ProjectConfiguration;
  allOptions: any[];
  overrides: PricingOverrides;
}) {
  const breakdown = useMemo(() => {
    const complexity: ComplexityFactors = {
      laborComplexity: config.laborComplexity || "standard",
      plumbingRelocation: config.plumbingRelocation || false,
      customModifications: config.customModifications || false,
    };
    const manualAdj = parseFloat(config.manualAdjustment || "0") || 0;
    return calculatePrice(config, allOptions, complexity, manualAdj, config.manualAdjustmentNotes || "", overrides);
  }, [config, allOptions, overrides]);

  return breakdown;
}

export function QuoteComparison({
  scenarios,
  baseConfig,
  quoteId,
  isLocked,
  onScenariosChange,
  onSelectScenario,
  onBack,
}: QuoteComparisonProps) {
  const { options: allOptions } = useProductOptions();
  const { config: pricingConfig } = usePricing();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newMode, setNewMode] = useState<ScenarioMode>("derived");
  const [editingScenario, setEditingScenario] = useState<QuoteScenario | null>(null);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);

  const pricingOverrides: PricingOverrides = {
    systemBasePrices: pricingConfig.systemBasePrices,
    dimensionPresets: pricingConfig.dimensionPresets,
    materialMultipliers: pricingConfig.materialMultipliers,
    complexityAdders: pricingConfig.complexityAdders,
  };

  const activeScenarios = scenarios.filter((s) => !s.isArchived);
  const archivedScenarios = scenarios.filter((s) => s.isArchived);

  const getBreakdown = (config: ProjectConfiguration) => {
    const complexity: ComplexityFactors = {
      laborComplexity: config.laborComplexity || "standard",
      plumbingRelocation: config.plumbingRelocation || false,
      customModifications: config.customModifications || false,
    };
    const manualAdj = parseFloat(config.manualAdjustment || "0") || 0;
    return calculatePrice(config, allOptions, complexity, manualAdj, config.manualAdjustmentNotes || "", pricingOverrides);
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    const config = newMode === "derived" && baseConfig
      ? { ...baseConfig }
      : { ...DEFAULT_CONFIG };
    const breakdown = getBreakdown(config);
    const scenario: QuoteScenario = {
      id: `SCN-${Date.now()}`,
      name: newName.trim(),
      mode: newMode,
      config,
      calculatedTotal: breakdown.finalTotal,
      isSelected: false,
      isArchived: false,
      createdAt: new Date().toISOString(),
    };
    onScenariosChange([...scenarios, scenario]);
    setCreateDialogOpen(false);
    setNewName("");
    // Open configurator for the new scenario
    setEditingScenario(scenario);
    setConfiguratorOpen(true);
    toast.success(`Scenario "${scenario.name}" created`);
  };

  const handleDelete = (id: string) => {
    onScenariosChange(scenarios.filter((s) => s.id !== id));
    toast("Scenario removed");
  };

  const handleArchive = (id: string) => {
    onScenariosChange(
      scenarios.map((s) => s.id === id ? { ...s, isArchived: true, isSelected: false } : s)
    );
    toast("Scenario archived");
  };

  const handleSelect = (scenario: QuoteScenario) => {
    // Mark this one as selected, unmark others
    const updated = scenarios.map((s) => ({ ...s, isSelected: s.id === scenario.id }));
    onScenariosChange(updated);
    onSelectScenario(scenario);
    toast.success(`"${scenario.name}" selected as final configuration`);
  };

  const handleEdit = (scenario: QuoteScenario) => {
    setEditingScenario(scenario);
    setConfiguratorOpen(true);
  };

  const handleConfigSave = (config: ProjectConfiguration) => {
    if (!editingScenario) return;
    const breakdown = getBreakdown(config);
    onScenariosChange(
      scenarios.map((s) =>
        s.id === editingScenario.id
          ? { ...s, config, calculatedTotal: breakdown.finalTotal }
          : s
      )
    );
    setEditingScenario(null);
    setConfiguratorOpen(false);
    toast.success("Scenario updated");
  };

   // Summary row helper
   const getSummary = (config: ProjectConfiguration) => {
     const items: { label: string; value: string }[] = [];
     if (config.bathroomScopes.length) items.push({ label: "Scope", value: config.bathroomScopes.join(", ") });
     if (config.bathroomSystemType) items.push({ label: "System", value: config.bathroomSystemType });
     if (config.bathroomMaterials.length) items.push({ label: "Material", value: config.bathroomMaterials.join(", ") });
     if (config.baseWidth && config.baseLength) items.push({ label: "Size", value: `${config.baseWidth}" × ${config.baseLength}"` });
     if (config.selectedWallSystem) items.push({ label: "Wall", value: allOptions.find(o => o.id === config.selectedWallSystem)?.displayName || config.selectedWallSystem });
     if (config.selectedGlass) items.push({ label: "Glass", value: allOptions.find(o => o.id === config.selectedGlass)?.displayName || config.selectedGlass });
     if (config.selectedDoor) items.push({ label: "Door", value: allOptions.find(o => o.id === config.selectedDoor)?.displayName || config.selectedDoor });
     return items;
   };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 h-8 text-xs">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Quote
          </Button>
          <div>
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" /> Compare Options
            </h2>
            <p className="text-xs text-muted-foreground">Create and compare different build scenarios for {quoteId}</p>
          </div>
        </div>
        {!isLocked && (
          <Button size="sm" className="gap-1.5" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> New Scenario
          </Button>
        )}
      </div>

      {/* Scenarios Grid */}
      {activeScenarios.length === 0 ? (
        <Card className="shadow-none">
          <CardContent className="p-8 text-center">
            <Layers className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No comparison scenarios yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Create scenarios to compare different build options side-by-side
            </p>
            {!isLocked && (
              <Button size="sm" className="mt-4 gap-1.5" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Create First Scenario
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activeScenarios.map((scenario) => {
            const breakdown = getBreakdown(scenario.config);
            const summary = getSummary(scenario.config);
            return (
              <Card
                key={scenario.id}
                className={`shadow-none transition-all ${
                  scenario.isSelected
                    ? "ring-2 ring-primary border-primary"
                    : "hover:border-primary/30"
                }`}
              >
                <CardContent className="p-4">
                  {/* Scenario header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold truncate">{scenario.name}</h3>
                        {scenario.isSelected && (
                          <Badge className="bg-primary/10 text-primary border-primary/30 text-[9px] gap-0.5">
                            <Star className="h-2.5 w-2.5" /> Selected
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[9px]">
                          {scenario.mode === "derived" ? (
                            <><Copy className="h-2 w-2 mr-0.5" /> Derived</>
                          ) : (
                            <><Layers className="h-2 w-2 mr-0.5" /> Independent</>
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Config summary */}
                  <div className="space-y-1 mb-3">
                    {summary.length > 0 ? (
                      summary.map((item) => (
                        <div key={item.label} className="flex justify-between text-xs">
                          <span className="text-muted-foreground">{item.label}</span>
                          <span className="font-medium text-right max-w-[60%] truncate">{item.value}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground/60 italic">Not configured yet</p>
                    )}
                  </div>

                  {/* Price breakdown mini */}
                  <div className="rounded-lg bg-muted/30 border p-3 mb-3">
                    <div className="space-y-1 text-xs">
                      {breakdown.baseSystemPrice > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Base ({breakdown.systemType})</span>
                          <span>${breakdown.baseSystemPrice.toLocaleString()}</span>
                        </div>
                      )}
                      {breakdown.materialAdjustment > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Material</span>
                          <span>+${breakdown.materialAdjustment.toLocaleString()}</span>
                        </div>
                      )}
                      {breakdown.componentTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Components</span>
                          <span>+${breakdown.componentTotal.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t">
                      <span className="text-sm font-bold">Total</span>
                      <span className="text-lg font-bold text-primary">
                        {breakdown.finalTotal > 0 ? `$${breakdown.finalTotal.toLocaleString()}` : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  {!isLocked && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 flex-1" onClick={() => handleEdit(scenario)}>
                        <Edit className="h-3 w-3" /> Edit
                      </Button>
                      {!scenario.isSelected && (
                        <Button size="sm" className="h-7 text-[10px] gap-1 flex-1" onClick={() => handleSelect(scenario)}>
                          <CheckCircle className="h-3 w-3" /> Select
                        </Button>
                      )}
                      {!scenario.isSelected && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => handleArchive(scenario.id)}>
                          <Archive className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Side-by-side comparison table for 2+ scenarios */}
      {activeScenarios.length >= 2 && (
        <Card className="shadow-none overflow-hidden">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b bg-muted/20">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Side-by-Side Comparison</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/10">
                    <th className="text-left p-3 font-semibold text-muted-foreground min-w-[120px]">Attribute</th>
                    {activeScenarios.map((s) => (
                      <th key={s.id} className="text-center p-3 font-semibold min-w-[140px]">
                        <div className="flex items-center justify-center gap-1">
                          {s.name}
                          {s.isSelected && <Star className="h-3 w-3 text-primary" />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Scope", fn: (c: ProjectConfiguration) => c.bathroomScopes.join(", ") || "—" },
                    { label: "System Type", fn: (c: ProjectConfiguration) => c.bathroomSystemType || "—" },
                    { label: "Material", fn: (c: ProjectConfiguration) => c.bathroomMaterials.join(", ") || "—" },
                    { label: "Dimensions", fn: (c: ProjectConfiguration) => c.baseWidth && c.baseLength ? `${c.baseWidth}" × ${c.baseLength}"` : "—" },
                    { label: "Wall System", fn: (c: ProjectConfiguration) => c.selectedWallSystem ? (allOptions.find(o => o.id === c.selectedWallSystem)?.displayName || "—") : "—" },
                    { label: "Glass", fn: (c: ProjectConfiguration) => c.selectedGlass ? (allOptions.find(o => o.id === c.selectedGlass)?.displayName || "—") : "—" },
                    { label: "Door", fn: (c: ProjectConfiguration) => c.selectedDoor ? (allOptions.find(o => o.id === c.selectedDoor)?.displayName || "—") : "—" },
                    { label: "Fixture", fn: (c: ProjectConfiguration) => c.selectedFixture ? (allOptions.find(o => o.id === c.selectedFixture)?.displayName || "—") : "—" },
                    { label: "Add-ons", fn: (c: ProjectConfiguration) => c.selectedAddOns.length > 0 ? `${c.selectedAddOns.length} items` : "—" },
                  ].map((row) => (
                    <tr key={row.label} className="border-b last:border-0">
                      <td className="p-3 text-muted-foreground font-medium">{row.label}</td>
                      {activeScenarios.map((s) => (
                        <td key={s.id} className="p-3 text-center">{row.fn(s.config)}</td>
                      ))}
                    </tr>
                  ))}
                  {/* Price row */}
                  <tr className="border-t-2 bg-muted/10">
                    <td className="p-3 font-bold">Final Price</td>
                    {activeScenarios.map((s) => {
                      const bd = getBreakdown(s.config);
                      return (
                        <td key={s.id} className="p-3 text-center">
                          <span className="text-base font-bold text-primary">
                            {bd.finalTotal > 0 ? `$${bd.finalTotal.toLocaleString()}` : "—"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Archived scenarios */}
      {archivedScenarios.length > 0 && (
        <div className="pt-3">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Archive className="h-3 w-3" /> Archived Scenarios ({archivedScenarios.length})
          </p>
          <div className="space-y-2">
            {archivedScenarios.map((s) => {
              const bd = getBreakdown(s.config);
              return (
                <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-muted/20 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-muted-foreground">{s.name}</span>
                    <Badge variant="outline" className="text-[9px]">{s.mode}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{bd.finalTotal > 0 ? `$${bd.finalTotal.toLocaleString()}` : "—"}</span>
                    {!isLocked && (
                      <Button size="sm" variant="ghost" className="h-6 text-[9px] text-destructive" onClick={() => handleDelete(s.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Scenario Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> New Comparison Scenario
            </DialogTitle>
            <DialogDescription>
              Create a new build scenario to compare alongside existing options.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium mb-1 block">Scenario Name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder='e.g., "Dekton Upgrade", "Full Remodel Option"'
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-2 block">Scenario Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  className={`p-3 rounded-lg border text-left transition-all ${
                    newMode === "derived"
                      ? "ring-2 ring-primary border-primary bg-primary/5"
                      : "hover:border-primary/30"
                  }`}
                  onClick={() => setNewMode("derived")}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Copy className="h-4 w-4 text-primary" />
                    <span className="text-xs font-bold">Derived</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    Starts from current config — modify material, glass, add-ons
                  </p>
                </button>
                <button
                  className={`p-3 rounded-lg border text-left transition-all ${
                    newMode === "independent"
                      ? "ring-2 ring-primary border-primary bg-primary/5"
                      : "hover:border-primary/30"
                  }`}
                  onClick={() => setNewMode("independent")}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 text-primary" />
                    <span className="text-xs font-bold">Independent</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    Start fresh — full scope, system, and build reconfiguration
                  </p>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setNewName(""); }}>Cancel</Button>
            <Button className="gap-1.5" onClick={handleCreate} disabled={!newName.trim()}>
              <Plus className="h-3.5 w-3.5" /> Create & Configure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scenario Configurator */}
      {editingScenario && (
        <ProjectConfigurator
          open={configuratorOpen}
          onOpenChange={(open) => {
            setConfiguratorOpen(open);
            if (!open) setEditingScenario(null);
          }}
          job={{ id: quoteId, customerName: "Scenario", payoutAmount: 0 } as any}
          config={editingScenario.config}
          onSave={handleConfigSave}
        />
      )}
    </div>
  );
}
